import React, { useState, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Collapse, { Panel } from 'rc-collapse';

import { C } from '@constants';
import {
  getEarlierDateOption,
  formatDateMedium,
  isSameDay,
  dateIsToday,
  splitNotificationFlight,
} from '@helpers';
import { flightShape } from '@types';
import { flightsAndAirlines as strings } from 'core/i18n';
import mapDispachToProps from 'core/redux/mapDispatchToProps';
import {
  getFlightsList,
  getFlightsByAirport,
  getAirlines,
} from 'core/redux/selectors';

import ProgressBar from 'core/components/ProgressBar/ProgressBar';
import SecurityWaitingTimeNow from 'core/components/SecurityWaitingTime/SecurityWaitingTimeNow';
import FlightsAndAirlinesTabs from './FlightsAndAirlinesTabs';
import FlightsAndAirlinesSearch from './FlightsAndAirlinesSearch';
import AirlinesHeader from './AirlinesHeader';
import AirlinesRowHeader from './AirlinesRowHeader';
import AirlinesRowDetails from './AirlinesRowDetails';
import FlightsRow from './FlightsRow';
import AirlinesTableForA11y from './AirlinesTableForA11y';
import Icon from 'core/components/Icon';
import InfoBanner from 'core/components/InfoBanner';
import { setActiveFlightDetails } from 'core/redux/actions';

const FlightsAndAirlinesList = ({
  activeTab,
  flightList,
  airlineList,
  airport,
  noFlightsAvailable,
  searchFlightsAndAirlines,
  keywords,
  date,
  apiGetAirlines,
  changeFlightsAndAirlinesTabs,
}) => {
  const [upperListBoundary, setUpperListBoundary] = useState(30);
  const [lowerListBoundary, setLowerListBoundary] = useState(0);
  const [reachedLowerLimit, setReachedLowerLimit] = useState(false);
  const [isFlightParamMatched, setIsFlightParamMatched] = useState(false);
  const startingSdt = useRef('');

  const dispatch = useDispatch();
  const checkFlightParam = () => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab') ?? C.FLIGHTS_QUERY_PARAMS.DEP;
    const notificationParams = params.get('flight') ?? '';
    changeFlightsAndAirlinesTabs(tab);
    if (notificationParams !== '') {
      const flightInfo = splitNotificationFlight(notificationParams);
      const matchedFlight = flightList.flights.find(
        (obj) => obj.id === notificationParams
      );
      if (matchedFlight) {
        setIsFlightParamMatched(true);
        dispatch(
          setActiveFlightDetails({
            tag: flightInfo.tag,
            homeAirport:
              flightInfo.arrOrDep === 'dep'
                ? matchedFlight.originAirport.iata ??
                  matchedFlight.originAirport.iataCode
                : matchedFlight.destinationAirport.iata ??
                  matchedFlight.destinationAirport.iataCode,
            arrOrDep: flightInfo.arrOrDep,
          })
        );
      } else {
        searchFlightsAndAirlines(keywords, flightInfo.date);
      }
    }
  };
  useEffect(() => {
    apiGetAirlines();
  }, []);

  useEffect(() => {
    if (upperListBoundary > flightList.flights.length) {
      setUpperListBoundary(flightList.flights.length);
      if (flightList.flights.length <= 30) {
        setLowerListBoundary(0);
      }
    }

    if (upperListBoundary === 0 && lowerListBoundary === 0) {
      setUpperListBoundary(30);
    }
  }, [activeTab]);

  useEffect(() => {
    if (date !== '' && !dateIsToday(date)) {
      setOlderFlightsLoaded(true);
      setLowerListBoundary(0);
      setShowEarlier(false);
      setReachedLowerLimit(true);
    }
    if (olderFlightsLoaded && lowerListBoundary === 0 && !reachedLowerLimit) {
      const sdtIndex = flightList.flights.findIndex(
        (flight) => flight.sdt === startingSdt.current
      );
      if (sdtIndex > 10) {
        setLowerListBoundary(sdtIndex - 10);
        setShowEarlier(true);
      } else {
        setShowEarlier(false);
        setLowerListBoundary(0);
      }

      upperListBoundary <= flightList.flights.length + 30
        ? setUpperListBoundary(sdtIndex + upperListBoundary)
        : setUpperListBoundary(flightList.flights.length);
    }

    if (upperListBoundary > flightList.flights.length) {
      setUpperListBoundary(flightList.flights.length);
    }

    if (lowerListBoundary > upperListBoundary) {
      upperListBoundary - 30 > 0 && upperListBoundary - 30 < 200
        ? setLowerListBoundary(upperListBoundary - 30)
        : setLowerListBoundary(0);
    }

    if (
      lowerListBoundary === 0 &&
      upperListBoundary === 0 &&
      flightList.flights.length > 0
    ) {
      setUpperListBoundary(30);
    }
    if (!isFlightParamMatched) {
      checkFlightParam();
    }
  }, [flightList.flights]);

  const earlierDateOption = getEarlierDateOption();
  const [showEarlier, setShowEarlier] = useState(true);
  const [olderFlightsLoaded, setOlderFlightsLoaded] = useState(false);
  const handleShowEarlierFlights = () => {
    if (olderFlightsLoaded) {
      if (lowerListBoundary > 10) {
        setLowerListBoundary(lowerListBoundary - 10);
      } else {
        setLowerListBoundary(0);
        setShowEarlier(false);
        setReachedLowerLimit(true);
      }
    } else {
      startingSdt.current = flightsFiltered[0]?.sdt;
      searchFlightsAndAirlines(keywords, earlierDateOption);
      setOlderFlightsLoaded(true);
    }
  };

  const handleShowLaterFlights = () => {
    if (upperListBoundary + 50 > flightList.flights.length) {
      setUpperListBoundary(upperListBoundary + 50);
    } else {
      setUpperListBoundary(flightList.flights.length);
    }
  };

  if (upperListBoundary > flightList.flights.length) {
    setUpperListBoundary(flightList.flights.length);
    if (flightList.flights.length <= 30) {
      setLowerListBoundary(0);
    }
  }

  const flightsFiltered = flightList.flights
    .filter((flight) => isSameDay(date, flight.sdt) || dateIsToday(flight.sdt))
    .slice(lowerListBoundary, upperListBoundary);

  const showSecurityWaitingTime =
    (C.IS_REALTIME_SECURITY_WAITING_TIME_ENABLED ||
      C.IS_PREDICTED_SECURITY_WAITING_TIME_ENABLED) &&
    airport === 'hel';

  return (
    <div className="flights-and-airlines rw-grid">
      <div className="rw-row">
        <div className="rw-col--md-6 rw-col--offset-md-1 rw-col--lg-12 rw-col--offset-lg-0 rw-col--xl-8 rw-col--offset-xl-2">
          <div className="flights-and-airlines-filters">
            <FlightsAndAirlinesTabs isInOverlay={false} />
            <FlightsAndAirlinesSearch />
            {activeTab === C.FLIGHTS_QUERY_PARAMS.DEP && airport === 'hel' && (
              <div className="flights-and-airlines-stay-updated">
                <Icon icon="airplane" className="rw-icon--md" />
                {strings.stayUpdatedMessage}
              </div>
            )}
            {showSecurityWaitingTime && (
              <div className="flights-and-airlines-filters__security-waiting-time">
                <SecurityWaitingTimeNow />
              </div>
            )}
          </div>
        </div>
      </div>
      {activeTab !== 'airlines' ? (
        <div className="flight-and-airlines-container">
          {showEarlier === true && (
            <div className="rw-row">
              <div className="flights-and-airlines__show-earlier-button">
                <button
                  className="rw-button rw-button--plain-blue rw-button--icon center"
                  onClick={() => {
                    handleShowEarlierFlights();
                  }}
                >
                  {strings.showEarlierFlightsButtonLabel}
                  <Icon
                    icon="arrow-up"
                    className="rw-icon--md@sm rw-icon--xl@md"
                  />
                </button>
              </div>
            </div>
          )}
          <div className="rw-row">
            <div className="rw-col--md-6 rw-col--offset-md-1 rw-col--lg-12 rw-col--offset-lg-0 rw-col--xl-10 rw-col--offset-xl-1">
              {flightList.isLoading && <ProgressBar />}
              <div className="flights_rows-container">
                {flightsFiltered.length > 0
                  ? flightsFiltered.reduce(
                      (acc, cur, i, arr) => [
                        ...acc,
                        ...(i === 0 || !isSameDay(cur.sdt, arr[i - 1].sdt)
                          ? [
                              <div
                                className="flights__date-divider"
                                key={`divider-${i}`}
                              >
                                {formatDateMedium(cur.sdt)}
                              </div>,
                              <div className="flights__row-header" key={`${i}`}>
                                <div className="flights__row-header-time">
                                  {strings.time}
                                </div>
                                <div className="flights__row-header-destination">
                                  {activeTab === C.FLIGHTS_QUERY_PARAMS.DEP
                                    ? strings.destination
                                    : strings.origin}
                                </div>
                                <div className="flights__row-header-status">
                                  {strings.status}
                                </div>
                              </div>,
                            ]
                          : []),
                        <FlightsRow
                          {...cur}
                          key={`${cur.tag}-${i}`}
                          isDepartingFlight={
                            activeTab === C.FLIGHTS_QUERY_PARAMS.DEP
                          }
                        />,
                      ],
                      []
                    )
                  : !flightList.isLoading && (
                      <p className="flights-and-airlines__no-results">
                        {noFlightsAvailable
                          ? strings.noFlightsAvailable
                          : strings.noFlightSearchResults}
                      </p>
                    )}
              </div>
            </div>
          </div>
          <div className="rw-row">
            <div className="flights-and-airlines__show-later-button">
              {flightList.flights.length > upperListBoundary && (
                <button
                  className="rw-button rw-button--plain-blue rw-button--icon center"
                  onClick={() => handleShowLaterFlights()}
                >
                  {strings.showLaterFlightsButtonLabel}
                  <Icon
                    icon="arrow-down"
                    className="rw-icon--md@sm rw-icon--xl@md"
                  />
                </button>
              )}
            </div>
          </div>
          <div className="rw-row">
            <div className="rw-col--md-6 rw-col--offset-md-1 rw-col--xl-8 rw-col--offset-xl-2 flights__info-banner">
              <InfoBanner title={strings.disclaimerTitle}>
                {strings.disclaimerBody}
              </InfoBanner>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flights__info-text">{strings.airlinesInfoText}</div>
          <AirlinesTableForA11y airlines={airlineList.airlines} />
          <div className="rw-row">
            <div className="rw-col--md-6 rw-col--offset-md-1 rw-col--lg-12 rw-col--offset-lg-0 rw-col--xl-10 rw-col--offset-xl-1">
              <AirlinesHeader />
              {airlineList.isLoading && <ProgressBar />}
              {airlineList.airlines.length > 0 ? (
                <Collapse accordion={true}>
                  {airlineList.airlines.map((airline, i) => (
                    <Panel
                      header={<AirlinesRowHeader {...airline} />}
                      showArrow={false}
                      headerClass="list__item__summary"
                      className="list__item--collapsible airline"
                      key={`${airline.code}-${i}`}
                    >
                      <AirlinesRowDetails {...airline} />
                    </Panel>
                  ))}
                </Collapse>
              ) : (
                !airlineList.isLoading && (
                  <p className="flights-and-airlines__no-results">
                    {strings.noAirlinesSearchResults}
                  </p>
                )
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

FlightsAndAirlinesList.propTypes = {
  flightList: PropTypes.shape({
    flights: PropTypes.arrayOf(PropTypes.shape(flightShape)),
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  airlineList: PropTypes.shape({
    airlines: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  activeTab: PropTypes.string.isRequired,
  apiGetAirlines: PropTypes.func.isRequired,
  changeFlightsAndAirlinesTabs: PropTypes.func.isRequired,
  airport: PropTypes.string.isRequired,
  noFlightsAvailable: PropTypes.bool.isRequired,
  searchFlightsAndAirlines: PropTypes.func.isRequired,
  keywords: PropTypes.string,
  date: PropTypes.string,
};

function mapStateToProps(state) {
  const flightList = getFlightsList(state);
  const activeTab = state.uiState.flightsAndAirlinesTab;

  return {
    activeTab,
    airlineList: {
      airlines: getAirlines(state),
      isLoading: state.airlineList.isLoading,
    },
    flightList: {
      flights: getFlightsByAirport(state),
      isLoading: state.flightList.flightsByAirports.find(
        ({ code }) => code === C.FLIGHTS_AIRPORT
      ).isLoading,
    },
    airport: C.FLIGHTS_AIRPORT,
    noFlightsAvailable: !flightList || !flightList.length,
    keywords: state.flightAndAirlineSearch.keywords,
    date: state.flightAndAirlineSearch.date,
  };
}

export default connect(
  mapStateToProps,
  mapDispachToProps
)(FlightsAndAirlinesList);
